<template>
  <section class="faq-container">
    <div
      class="content-wrapper"
      :style="{
        '--bg-image': `url(${getCDNlink('assets/new-assets/Faq_Bg.webp')})`,
      }"
    >
      <div class="left-section">
        <h1 class="title">Your Questions Answered</h1>
        <p class="subtitle">
          Find answers to common questions about Credence and our products.
        </p>
      </div>

      <div class="right-section">
        <div class="faq-list">
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="faq-item"
            :class="{ active: activeIndex === index }"
          >
            <button class="faq-question" @click="toggleFaq(index)">
              <span>{{ faq.question }}</span>
              <svg
                class="chevron"
                :class="{ rotated: activeIndex === index }"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <div
              class="faq-answer"
              :class="{ expanded: activeIndex === index }"
            >
              <div class="answer-content">
                {{ faq.answer }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import config from "@/config";

export default {
  name: "FaqComponent",
  data() {
    return {
      activeIndex: 0, // First FAQ open by default
      faqs: [
        {
          question: "What is Credence Data?",
          answer:
            "Credence Data is a leading AI-powered business intelligence platform delivering almost real-time credit reports, advanced counterparty due diligence, and comprehensive global trade analytics. We empower banks, importers, exporters, and financial institutions to proactively manage risk and streamline decision-making processes.",
        },
        {
          question: "How does Credence Data use AI in business intelligence?",
          answer:
            "Credence Data leverages cutting-edge artificial intelligence to provide instant, predictive insights through real-time analytics, conversational AI-driven reports, and continuous data monitoring. Our AI identifies risks proactively, automates compliance checks, and significantly reduces manual processing efforts.",
        },
        {
          question: "Who can benefit from Credence Data’s solutions?",
          answer:
            "Banks, importers, exporters, financial institutions, insurance companies, and risk management professionals can significantly benefit from Credence Data’s AI-driven solutions. Our platform streamlines compliance, enhances due diligence, and proactively mitigates risks.",
        },
        {
          question:
            "What makes Credence Data’s conversational AI reports unique?",
          answer:
            "Our conversational AI technology “Insight Lens” enables users to interact with reports using simple natural language queries. Users can instantly access precise insights, drastically reducing time spent analysing complex data and making decision-making faster and more accurate.",
        },
        {
          question: "How quickly can Credence Data deliver credit reports?",
          answer:
            "Credence Data provides fresh and almost real time data for even SMES/ MSMEs in Asia. It’s within one hour for core countries. For non core countries, it varies from 1 to 3 days. Reports are continuously updated, ensuring our clients always receive the latest intelligence.",
        },
        {
          question:
            "Can Credence Data integrate with existing enterprise systems?",
          answer:
            "Yes, Credence Data offers robust AI-integrated APIs that seamlessly connect our intelligence platform with existing enterprise CRMs, ERPs, compliance, and risk management tools, allowing businesses to embed real-time intelligence directly into their workflow.",
        },
        {
          question:
            "What type of trade fraud detection does Credence Data’s AI provide?",
          answer:
            "Credence Data’s AI-driven fraud detection tools proactively identify the actual trade between counterparties and provide details around goods, shipment frequency, counterparties etc.",
        },
        {
          question: "How secure is Credence Data’s platform?",
          answer:
            "Credence Data adheres to the highest security standards, ensuring data protection through advanced encryption, strict data governance policies, and regular security audits. Our secure cloud infrastructure protects sensitive business information from unauthorized access.",
        },
        {
          question: "How can I start using Credence Data?",
          answer:
            "Getting started is simple. Create a Free Login that provides access to Free Status of the all the companies in our platform. Request a free demo through our website, and our team will guide you through platform features tailored to your organization's specific needs.",
        },
        {
          question:
            "How is Credence Data different from traditional BI providers?",
          answer:
            "Our differentiators are Fresh and Almost real time data, real time trade information of the counterparty, an easy to understand summary and integrated KYC and AML to give a comprehensive view. Unlike traditional BI providers, Credence Data combines AI-powered real-time analytics, conversational reporting, and seamless integration capabilities. Our platform delivers instant actionable insights, proactive risk management, and superior user experience, setting a new industry benchmark.",
        },
      ],
    };
  },
  methods: {
    toggleFaq(index) {
      // If clicking the same FAQ that's already open, close it
      if (this.activeIndex === index) {
        this.activeIndex = null;
      } else {
        // Otherwise, open the clicked FAQ
        this.activeIndex = index;
      }
    },
    getCDNlink(fileKey) {
      let baseUrl = "";
      if (
        process.env.NODE_ENV === "development" ||
        process.env.NODE_ENV === "staging"
      ) {
        baseUrl = config.stagingCDN;
      } else if (process.env.NODE_ENV === "production") {
        baseUrl = config.prodCDN;
      }

      return `${baseUrl}${fileKey}`;
    },
  },
};
</script>

<style scoped>
.faq-container {
  background: linear-gradient(180deg, #046BE1 0%, #01428e 100%);
  padding: 80px 140px;
}

.content-wrapper {
  position: relative;
  display: flex;
  padding: 40px;
  justify-content: center;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
  border-radius: 15px;
  background: linear-gradient(
    270deg,
    rgba(18, 18, 18, 0.25) 14%,
    rgba(28, 28, 28, 0.92) 100%
  );
  overflow: hidden;
  z-index: 1;
}

.content-wrapper::before {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--bg-image) center/cover no-repeat;
  mask-image: linear-gradient(to right, black -600%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, black -600%, transparent 100%);
  background-blend-mode: multiply;
  z-index: 0;
}

.content-wrapper > * {
  position: relative;
  z-index: 2;
}

.left-section {
  padding: 0;
  border-radius: 24px;
  text-align: left;
}

.title {
  font-family: "Figtree";
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: 56px;
  letter-spacing: 0.56px;
  margin: 0;
  background: var(
    --Home-Banner-Title-Gradient,
    radial-gradient(
      58.47% 140.95% at 31.38% -13.87%,
      #fff 36%,
      #ffece0 64%,
      #72b4ff 88.94%
    )
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.subtitle {
  color: #fff;
  font-family: "Figtree";
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.018px;
}

.right-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 80%;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.faq-item {
  /* background: rgba(30, 41, 59, 0.9); */
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  text-align: left;
}

.faq-item.active {
  border-radius: 8px;
  border: 1px solid #046BE1c7;
  background: var(
    --FAQ-Gradient,
    radial-gradient(71.02% 71.03% at 16.95% 16.51%, #0f0d43 0%, #1d1016 100%)
  );
}

.faq-question {
  width: 100%;
  padding: 24px 28px;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
  height: auto;
}

/* .faq-question:hover {
  background: rgba(255, 255, 255, 0.05);
} */

.chevron {
  color: #ffffff;
  transition: transform 0.3s ease;
  flex-shrink: 0;
  margin-left: 16px;
}

.chevron.rotated {
  transform: rotate(180deg);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.faq-answer.expanded {
  max-height: 500px;
}

.answer-content {
  padding: 0 28px 28px 28px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.6;
}

/* Responsive Design */
@media only screen and (min-width: 768px) and (max-width: 1023px) {
  .content-wrapper {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .title {
    font-size: 2.5rem;
  }

  .left-section {
    padding: 40px 30px;
  }
}

@media (max-width: 767px) {
  .faq-container {
    padding: 45px 40px;
  }
  .content-wrapper {
    flex-direction: column;
    padding: 20px;
  }
  .title {
    font-size: 2rem;
  }

  .left-section {
    border-radius: 16px;
  }
  .subtitle {
    margin: 0;
  }
  .faq-question {
    padding: 10px 24px;
    font-size: 1rem;
  }
  .right-section {
    width: 100%;
  }
  .answer-content {
    padding: 0 24px 24px 24px;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .faq-container {
    padding: 25px 18px;
  }

  .content-wrapper {
    gap: 15px;
    padding: 10px 12px;
  }

  .title {
    font-size: 36px;
    line-height: 36px;
  }
.faq-list{
  gap:10px;
}
  .subtitle {
    font-size: 1rem;
    margin-top: 8px;
    line-height: 20px;
  }

  .left-section {
    padding: 14px;
  }

  .faq-question {
    padding: 14px 20px;
  }

  .answer-content {
    padding: 0 20px 20px 20px;
  }
}
</style>
